/**
 * Stone Investment Contact Map Hero
 * 
 * Map-based hero for contact page with Stone Investment's luxury aesthetic:
 * - Full-width map background
 * - Contact info overlay
 * - Elegant typography
 */

'use client';

import { MapPin, Phone, Mail, Clock } from 'lucide-react';

export interface StoneContactMapHeroProps {
  /**
   * Page title
   */
  title?: string;
  /**
   * Page subtitle
   */
  subtitle?: string;
  /**
   * Office address
   */
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  };
  /**
   * Phone number
   */
  phone?: string;
  /**
   * Email address
   */
  email?: string;
  /**
   * Office hours
   */
  hours?: string;
  /**
   * Map coordinates
   */
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneContactMapHero - Map-based hero for contact page
 */
export function StoneContactMapHero({
  title = 'Contact Us',
  subtitle,
  address,
  phone,
  email,
  hours,
  coordinates,
  messages = {},
  className = '',
}: StoneContactMapHeroProps) {
  const googleMapsUrl = coordinates 
    ? `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`
    : undefined;
  
  return (
    <section className={`relative min-h-[60vh] bg-stone-200 ${className}`}>
      {/* Map Background Placeholder */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='%23d6d3d1' fill-opacity='0.3'/%3E%3C/svg%3E")`,
          backgroundSize: '30px 30px',
        }}
      />
      
      {/* Gradient Overlay - full coverage on mobile, directional on desktop */}
      <div className="absolute inset-0 bg-stone-900/80 md:bg-gradient-to-r md:from-stone-900/90 md:via-stone-900/70 md:to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 lg:py-40">
        <div className="max-w-xl">
          {/* Title */}
          <p className="text-xs uppercase tracking-[0.3em] text-stone-400 font-sans mb-4">
            {messages.getInTouch || 'Get in Touch'}
          </p>
          <h1 
            className="font-serif text-5xl lg:text-6xl text-white mb-6"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {title}
          </h1>
          
          {subtitle && (
            <p className="text-lg text-stone-300 font-sans mb-12">
              {subtitle}
            </p>
          )}
          
          {/* Contact Details */}
          <div className="space-y-6">
            {/* Address */}
            {address && (
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-stone-400 mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mb-2">
                    {messages.address || 'Address'}
                  </p>
                  <address className="not-italic text-white font-sans">
                    {address.line1 && <span className="block">{address.line1}</span>}
                    {address.line2 && <span className="block">{address.line2}</span>}
                    <span className="block">
                      {[address.city, address.region, address.postalCode].filter(Boolean).join(', ')}
                    </span>
                    {address.country && <span className="block">{address.country}</span>}
                  </address>
                  {googleMapsUrl && (
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-sm text-stone-400 hover:text-white transition-colors underline"
                    >
                      {messages.viewOnMap || 'View on Map'}
                    </a>
                  )}
                </div>
              </div>
            )}
            
            {/* Phone */}
            {phone && (
              <div className="flex items-start gap-4">
                <Phone size={20} className="text-stone-400 mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mb-2">
                    {messages.telephone || 'Telephone'}
                  </p>
                  <a 
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="text-white font-sans hover:text-stone-300 transition-colors"
                  >
                    {phone}
                  </a>
                </div>
              </div>
            )}
            
            {/* Email */}
            {email && (
              <div className="flex items-start gap-4">
                <Mail size={20} className="text-stone-400 mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mb-2">
                    {messages.email || 'Email'}
                  </p>
                  <a 
                    href={`mailto:${email}`}
                    className="text-white font-sans hover:text-stone-300 transition-colors"
                  >
                    {email}
                  </a>
                </div>
              </div>
            )}
            
            {/* Hours */}
            {hours && (
              <div className="flex items-start gap-4">
                <Clock size={20} className="text-stone-400 mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mb-2">
                    {messages.officeHours || 'Office Hours'}
                  </p>
                  <p className="text-white font-sans">{hours}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default StoneContactMapHero;
